import { Timestamp } from 'firebase/firestore';

export type BlogPostStatus = 'draft' | 'published' | 'archived';

export interface BlogPostSEO {
  // Primary SEO
  metaTitle?: string; // Falls back to title if not set
  metaDescription: string; // Required for SEO
  metaKeywords: string[]; // Array of keywords
  focusKeyword: string; // Primary keyword to target
  canonicalUrl?: string; // Optional canonical URL

  // Open Graph (Facebook, LinkedIn)
  ogTitle?: string; // Falls back to title
  ogDescription?: string; // Falls back to metaDescription
  ogImage?: string; // Falls back to featuredImage
  ogType: string; // Usually 'article'

  // Twitter Card
  twitterCard: 'summary' | 'summary_large_image';
  twitterTitle?: string; // Falls back to title
  twitterDescription?: string; // Falls back to metaDescription
  twitterImage?: string; // Falls back to featuredImage
}

export interface BlogPostImage {
  url: string;
  alt: string;
  storagePath?: string; // Firebase Storage path for deletion
}

export interface BlogPost {
  id: string;
  slug: string; // URL-friendly unique identifier

  // Content
  title: string;
  content: string; // Markdown content
  excerpt?: string; // Short summary

  // Author
  author: string;
  authorEmail: string;

  // Featured Image
  featuredImage?: BlogPostImage;

  // Organization
  tags: string[];
  category?: string;
  readTime: string; // e.g., "5 min read"

  // SEO
  seo: BlogPostSEO;

  // Status & Publishing
  status: BlogPostStatus;
  publishedAt?: Timestamp;
  scheduledFor?: Timestamp; // Future: scheduled publishing

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;

  // Analytics
  views: number;
  lastViewedAt?: Timestamp;
}

export interface BlogPostFormData {
  slug: string;
  title: string;
  content: string;
  excerpt?: string;
  author: string;
  tags: string[];
  category?: string;
  readTime: string;
  featuredImage?: BlogPostImage;

  // SEO Fields
  metaDescription: string;
  metaKeywords: string[];
  focusKeyword: string;
  canonicalUrl?: string;

  // Optional SEO overrides
  metaTitle?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;

  status: BlogPostStatus;
  scheduledFor?: Date;
}

// Helper function to generate slug from title
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
}

// Helper to estimate read time
export function estimateReadTime(content: string): string {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
}
